import {
  TRUCK_DEFAULTS,
  newId,
  type CostProfile,
  type PayContract,
  type Truck,
} from "@haulsheet/core";
import { getDb } from "../db/dexie";
import type { MeResponse } from "../store/authStore";
import { getSyncClient } from "../sync/SyncClient";

/**
 * Values the API writes when it creates an org/driver (apps/api/src/auth/bootstrap.ts).
 * A row still holding these, never edited (no fieldMeta entry), was never set by the user.
 */
export const BOOTSTRAP_OTHER_CENTS_PER_MILE = 30;
export const BOOTSTRAP_CUT_BPS = 0;
export const BOOTSTRAP_CONTRACT_NAME = "Default lease";

export const MPG_MIN = 3;
export const MPG_MAX = 15;
/** Non-fuel cost per mile, dollars. Above this it's almost certainly cents typed as dollars. */
export const CPM_MAX_DOLLARS = 3;

export function setupDoneKey(orgId: string): string {
  return `firstRunSetup:${orgId}`;
}

export function setupSnoozeKey(orgId: string): string {
  return `hs.firstRunSetup.snooze:${orgId}`;
}

export interface SetupNeeds {
  mpg: boolean;
  costPerMile: boolean;
  cut: boolean;
}

/** Server sets row.fieldMeta[field] whenever a sync patch changes that field. */
function edited(row: object | null | undefined, field: string): boolean {
  const meta = (row as { fieldMeta?: unknown } | null | undefined)?.fieldMeta;
  return !!meta && typeof meta === "object" && field in (meta as Record<string, unknown>);
}

/** Has any active contract had its cut set (edited, non-zero, or a user/import-created contract)? */
export function cutIsSet(contracts: PayContract[]): boolean {
  const active = contracts.filter((c) => c.active && c.deletedAt == null);
  return active.some(
    (c) =>
      c.cutBps !== BOOTSTRAP_CUT_BPS ||
      edited(c, "cutBps") ||
      c.name !== BOOTSTRAP_CONTRACT_NAME,
  );
}

export function costPerMileIsSet(cost: CostProfile | null | undefined): boolean {
  if (!cost) return false;
  return (
    cost.otherCentsPerMile !== BOOTSTRAP_OTHER_CENTS_PER_MILE ||
    edited(cost, "otherCentsPerMile")
  );
}

/**
 * Which numbers still sit on defaults. Returns null when the card must not show:
 * no driver (dispatcher-only), read-only plan, data not pulled yet (no cost
 * profile for this driver), already completed, or snoozed this session.
 */
export function setupNeeds(args: {
  me: MeResponse | null;
  trucks: Truck[];
  cost: CostProfile | null | undefined;
  contracts: PayContract[];
  done: boolean;
  snoozed: boolean;
}): SetupNeeds | null {
  const { me } = args;
  if (!me || !me.driverId || me.plan.readOnly || args.done || args.snoozed) return null;
  if (!args.cost) return null;
  const needs: SetupNeeds = {
    mpg: !args.trucks.some((t) => t.deletedAt == null),
    costPerMile: !costPerMileIsSet(args.cost),
    // Drivers can't edit the pay contract (SettingsPay is read-only for them).
    cut:
      me.role !== "driver" &&
      args.contracts.some((c) => c.active && c.deletedAt == null) &&
      !cutIsSet(args.contracts),
  };
  return needs.mpg || needs.costPerMile || needs.cut ? needs : null;
}

export interface SetupInput {
  mpg: string;
  costPerMile: string;
  cutPct: string;
}

export interface SetupValues {
  mpg?: number;
  otherCentsPerMile?: number;
  cutBps?: number;
}

export type SetupErrors = Partial<Record<keyof SetupInput, string>>;

function num(raw: string): number | null {
  const t = raw.trim().replace(/[$,%¢\s]/g, "").replace(/\/mi$/i, "");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/** Validate only the fields this user is being asked for. */
export function validateSetup(
  input: SetupInput,
  needs: SetupNeeds,
): { ok: true; values: SetupValues } | { ok: false; errors: SetupErrors } {
  const errors: SetupErrors = {};
  const values: SetupValues = {};

  if (needs.mpg) {
    const n = num(input.mpg);
    if (n == null) errors.mpg = "Enter your truck’s MPG.";
    else if (Number.isNaN(n) || n < MPG_MIN || n > MPG_MAX) {
      errors.mpg = `MPG should be between ${MPG_MIN} and ${MPG_MAX}.`;
    } else values.mpg = Math.round(n * 10) / 10;
  }

  if (needs.costPerMile) {
    const n = num(input.costPerMile);
    if (n == null) errors.costPerMile = "Enter your cost per mile (not counting fuel).";
    else if (Number.isNaN(n) || n < 0) errors.costPerMile = "Cost per mile must be a number, e.g. 0.30.";
    else if (n > CPM_MAX_DOLLARS) {
      errors.costPerMile = `That’s $${n.toFixed(2)} a mile. Enter dollars, e.g. 0.30 for 30¢.`;
    } else values.otherCentsPerMile = Math.round(n * 100);
  }

  if (needs.cut) {
    const n = num(input.cutPct);
    if (n == null) errors.cutPct = "Enter your cut %. Use 0 if you keep the whole rate.";
    else if (Number.isNaN(n) || n < 0 || n >= 100) errors.cutPct = "Cut % should be from 0 to 99.";
    else values.cutBps = Math.round(n * 100);
  }

  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, values };
}

/**
 * Persist through the normal offline-first path (Dexie row + outbox, pushed
 * when online) — the same writes Settings → Truck / Costs / Pay make — then
 * mark setup done for this org on this device.
 */
export async function saveSetup(args: {
  me: MeResponse;
  values: SetupValues;
  truck: Truck | null;
  cost: CostProfile;
  contract: PayContract | null;
}): Promise<void> {
  const { me, values } = args;
  const client = getSyncClient();
  const now = new Date().toISOString();

  if (values.mpg != null) {
    if (args.truck) {
      await client.mutate({
        entity: "truck",
        op: "upsert",
        id: args.truck.id,
        baseVersion: args.truck.version || null,
        patch: { mpg: values.mpg },
        row: { ...args.truck, mpg: values.mpg, updatedAt: now },
      });
    } else {
      const id = newId();
      await client.mutate({
        entity: "truck",
        op: "upsert",
        id,
        baseVersion: null,
        row: {
          id,
          orgId: me.org.id,
          version: 0,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
          driverId: me.driverId,
          unitNumber: null,
          ...TRUCK_DEFAULTS,
          mpg: values.mpg,
        },
      });
    }
  }

  if (values.otherCentsPerMile != null) {
    const patch = { otherCentsPerMile: values.otherCentsPerMile };
    await client.mutate({
      entity: "cost_profile",
      op: "upsert",
      id: args.cost.id,
      baseVersion: args.cost.version || null,
      patch,
      row: { ...args.cost, ...patch, updatedAt: now },
    });
  }

  if (values.cutBps != null && args.contract) {
    const patch = { cutBps: values.cutBps };
    await client.mutate({
      entity: "pay_contract",
      op: "upsert",
      id: args.contract.id,
      baseVersion: args.contract.version || null,
      patch,
      row: { ...args.contract, ...patch, updatedAt: now },
    });
  }

  await getDb().meta.put({ key: setupDoneKey(me.org.id), value: now });
}

export function isSnoozed(orgId: string): boolean {
  try {
    return sessionStorage.getItem(setupSnoozeKey(orgId)) === "1";
  } catch {
    return false;
  }
}

export function snooze(orgId: string): void {
  try {
    sessionStorage.setItem(setupSnoozeKey(orgId), "1");
  } catch {
    /* private mode: card just shows again on next render */
  }
}
